import { z } from 'zod';
import { CommitmentRollup, ProjectCommitment } from './project-commitment.js';
import { ProjectRole } from './rbac.js';
import { ProjectRoleView } from './project-role.js';
/**
 * Thành viên dự án, nhìn từ **trong** dự án — APM-1309.
 *
 * ## Vì sao không dùng lại `ProjectMemberView` của màn hình quản trị
 *
 * `ProjectMemberView` (`admin-users.ts`) trả lời câu hỏi của quản trị viên: ai
 * ở trong dự án nào, vai trò gì. Tab cấu hình dự án hỏi câu khác — "đội này
 * đang gánh gì" — nên nó cần **số việc đang giao** và **giờ còn lại**, những
 * con số vô nghĩa ở màn hình kia.
 *
 * Thêm hai trường ấy vào kiểu cũ thì mọi chỗ dùng nó phải trả giá cho hai truy
 * vấn tổng hợp mà chúng không cần. Hai câu hỏi khác nhau, hai kiểu.
 *
 * ## Vì sao ở đây CÓ tên người, khác `TeamWorkloadView`
 *
 * Cùng ranh giới mà `APM-504b` đã dựng: kiểu này chỉ nói về **một** dự án mà
 * người gọi đã được vào, nên tên và số liệu của thành viên trong đó là bình
 * thường — đúng lượng thông tin mà board đã hiển thị. Còn tải **cộng dồn qua
 * mọi dự án** thì vẫn chỉ được trả về con số, không tên.
 */
export const ProjectMemberDetail = z.object({
    membershipId: z.uuid(),
    userId: z.uuid(),
    displayName: z.string(),
    email: z.string(),
    /** `null` = vai trò **tuỳ biến** của dự án đó — APM-ROLE-b. */
    roleKey: ProjectRole.nullable(),
    roleId: z.uuid(),
    roleName: z.string(),
    /** Phòng ban, để phân biệt hai người trùng tên — APM-1315. `null` = chưa gán. */
    departmentName: z.string().nullable(),
    joinedAt: z.iso.datetime(),
    /** Việc chưa xong đang mang tên người này **trong dự án này**. */
    openTaskCount: z.number().int().nonnegative(),
    /** Tổng ước lượng còn lại của những việc đó, tính bằng giờ. */
    openEstimateHours: z.number().nonnegative(),
    /**
     * Gỡ được khỏi dự án hay không, và vì sao không.
     *
     * Tính ở server chứ không để giao diện tự suy từ `openTaskCount`: cửa chặn
     * thật nằm trong command handler, và hai chỗ tự suy ra điều kiện sẽ lệch nhau
     * ngay khi thêm một điều kiện thứ ba — lúc đó nút hiện ra rồi bấm vào báo lỗi.
     */
    removableReason: z.enum(['ok', 'has_open_tasks', 'last_pm']),
    /**
     * Cộng tác viên **ngoài tổ chức** (`GUEST`) — APM-GUEST-e. Giao diện ghi nhãn
     * "Ngoài" cạnh tên và ẩn phần cam kết: khách không có lịch làm việc nào để cam kết.
     */
    isGuest: z.boolean().default(false),
    /**
     * Phần cam kết cho **dự án này** — APM-1310b. `null` = chưa đặt.
     *
     * Chưa đặt khác hẳn "0 giờ": nó nghĩa là chưa ai quyết, nên nó không tham gia
     * phép cộng dồn và giao diện phải nói ra bằng chữ, không bằng số 0.
     */
    commitment: ProjectCommitment.nullable(),
    /**
     * Cam kết ở những dự án **khác**, kèm trần từ lịch gốc — để cảnh báo cộng dồn.
     *
     * Đi cùng mỗi thành viên chứ không phải một mảng riêng: màn hình cần nó ở
     * đúng dòng đang gõ, và một mảng thứ hai phải ghép lại theo `userId` là một
     * chỗ nữa để ghép sai.
     */
    commitmentRollup: CommitmentRollup,
});
export const ProjectMembersView = z.object({
    projectId: z.uuid(),
    members: z.array(ProjectMemberDetail),
    /**
     * Vai trò của **chính dự án này** — hệ thống lẫn tuỳ biến (APM-ROLE-i).
     *
     * Trả kèm thay vì để giao diện gọi thêm một endpoint: ô chọn vai trò nằm trên
     * mỗi hàng thành viên, nên danh sách này luôn cần cùng lúc với danh sách
     * người. Hai lời gọi cho một màn hình còn tạo ra một khoảng thời gian mà ô
     * chọn hiện rỗng trong khi hàng đã vẽ xong.
     */
    roles: z.array(ProjectRoleView),
    /** Người trong tổ chức **chưa** ở dự án này — danh sách để chọn khi thêm. */
    candidates: z.array(z.object({
        userId: z.uuid(),
        displayName: z.string(),
        email: z.string(),
        departmentName: z.string().nullable(),
    })),
});
export const SetMemberRoleInput = z.object({
    userId: z.uuid(),
    /**
     * Id của một vai trò **thuộc chính dự án này**. `null` = gỡ khỏi dự án.
     *
     * Không còn là chuỗi enum: từ APM-ROLE-a một dự án có thể có vai trò tuỳ
     * biến, và chúng không có tên cố định nào để gửi qua dây.
     */
    roleId: z.uuid().nullable(),
});
/**
 * PM bàn giao vai của mình — APM-MAINT-c.
 *
 * Chỉ có người **nhận**: người giao luôn là người đang gọi. "PM này bàn giao
 * thay cho PM kia" là một quyền khác hẳn — nó cho phép tước vai của người ngang
 * hàng — và ai cần đổi vai người khác thì đã có `SetMemberRoleInput`.
 */
export const HandOverPmInput = z.object({ toUserId: z.uuid() });
/**
 * Tự rời dự án — APM-ROLE-q.
 *
 * ## ⚠️ Khác hẳn PM gỡ người, và khác hẳn nghỉ việc
 *
 * PM gỡ một người còn việc dở bị **chặn cứng** (`removableReason`): PM có lựa
 * chọn khác — chuyển việc trước rồi gỡ. Người **tự** rời thì không: chặn cứng
 * là nhốt họ trong một dự án họ không còn làm, và thứ họ sẽ làm thay là im
 * lặng bỏ mặc những việc vẫn mang tên mình.
 *
 * Nên đường này không chặn, nhưng việc còn lại **rơi về chưa giao**. Một việc
 * mang tên người không còn trong dự án là việc trông như có người làm mà không
 * ai làm — không màn hình nào kêu, không ai đi tìm. Chưa giao thì ít nhất nó
 * hiện ở hàng "chưa giao" của tab Điều phối.
 *
 * Nghỉ việc (`APM-1311b`) lại khác nữa: người đó rời **công ty**, tài khoản
 * khoá, và mọi dự án cùng lúc.
 */
export const LeaveProjectTask = z.object({
    taskId: z.uuid(),
    /** `PAY-12` — thứ người ta nhận ra, không phải uuid. */
    taskKey: z.string(),
    title: z.string(),
    statusName: z.string(),
    startDate: z.iso.date().nullable(),
    dueDate: z.iso.date().nullable(),
    estimateHours: z.number().nullable(),
});
/** Trần danh sách việc hiện trước khi rời — quá số này thì đếm, không liệt kê. */
export const MAX_LEAVE_PREVIEW_TASKS = 20;
export const LeaveProjectPreview = z.object({
    /** `false` = người gọi không ở trong dự án này, nên không có gì để rời. */
    isMember: z.boolean(),
    /**
     * PM cuối cùng thì **vẫn** bị chặn, khác với việc còn dở.
     *
     * Đây không phải cùng một loại hậu quả: bỏ lại việc chưa giao là một vấn đề
     * nhìn thấy được và sửa được, còn bỏ lại một dự án không có PM là bỏ lại một
     * dự án mà **không ai còn quyền** giao lại việc hay thêm người — kể cả để dọn
     * đúng mớ vừa tạo ra.
     */
    isLastPm: z.boolean(),
    /** Việc chưa xong đang mang tên người gọi, tối đa `MAX_LEAVE_PREVIEW_TASKS`. */
    tasks: z.array(LeaveProjectTask),
    openTaskCount: z.number().int().nonnegative(),
});
export const LeaveProjectResult = z.object({
    unassignedTasks: z.number().int().nonnegative(),
});
//# sourceMappingURL=project-member.js.map